// [S-413 follow-up] Shared read-only, paginated, sanitized HTML document
// viewer — the one "preview" renderer for the platform's openable records.
// Originally lived only inside TemplateDetail; extracted here so a certified
// template, a precedent, and a drafted contract all present their content
// through the SAME widget when opened from a list (Templates / Precedents /
// Drafts) or the Studio Agents record drawer, rather than three bespoke
// implementations drifting apart.
//
// The upload/generation pipelines store HTML (mammoth/marked output, or the
// draft generator's own <h2>/<p> markup) — sanitize via DOMPurify and render
// with dangerouslySetInnerHTML, otherwise raw tags show as text. Long
// documents are paginated by walking top-level block elements and
// accumulating them until the running visible-text length crosses a target
// that SCALES with the window size, so the reader gets explicit
// page-N-of-M navigation sized to actually use the screen.
import { useEffect, useMemo, useState } from "react";
import DOMPurify from "dompurify";
import { DocPager } from "./DocPager";

// [S-511] Some content arrives as HTML (the DOCX upload pipeline → mammoth/
// marked, or a generated draft's own markup), but seeded/certified templates
// are stored as PLAIN TEXT with newline breaks. Rendered straight through
// dangerouslySetInnerHTML, plain text collapses into one unreadable line.
// Detect plain text and convert it to paragraphs so the document is legible
// everywhere this renders.
// [citation-gap fix] `mark` added: DraftDetail.tsx's DraftPreview runs
// markCitations() on the draft's content BEFORE handing it to
// PaginatedDocument — if that content had no other recognized tag (a plain-
// text draft body with only a citation mark injected), the mark's own
// `<mark ...>` was the only HTML signal present, and without it here the
// whole thing fell through to plainTextToHtml's escapeHtml, turning the
// citation into inert escaped text instead of a real, clickable element.
function looksLikeHtml(s: string): boolean {
  return /<(p|h[1-6]|ul|ol|li|div|br|table|blockquote|strong|em|section|article|mark)\b/i.test(
    s
  );
}
function escapeHtml(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}
// Blank lines split paragraphs; single newlines become <br>; a leading numbered
// section label ("1. COMPENSATION.") is bolded so the structure reads clearly.
function plainTextToHtml(text: string): string {
  return text
    .split(/\n{2,}/)
    .map((para) => para.trim())
    .filter((para) => para.length > 0)
    .map((para) => {
      const withBreaks = para.split(/\n/).map(escapeHtml).join("<br>");
      const bolded = withBreaks.replace(
        /^(\d+\.\s+[A-Z][^.]{0,60}\.)/,
        "<strong>$1</strong>"
      );
      return `<p>${bolded}</p>`;
    })
    .join("");
}
function toRenderableHtml(content: string): string {
  return looksLikeHtml(content) ? content : plainTextToHtml(content);
}

// Base ~1500 visible chars per page ≈ a printed legal page at a ~1024×768
// window (jsdom's default size too, so this is also the default in tests).
// SCALED by the actual window area at render time — a wide/tall monitor gets
// a bigger page (fewer clicks through the same document); a small window
// gets a lighter one. Clamped so it never goes degenerate at either extreme.
const PAGE_TARGET_CHARS_BASE = 1500;
const PAGE_TARGET_REF_AREA = 1024 * 768;
const PAGE_TARGET_CHARS_MIN = 900;
const PAGE_TARGET_CHARS_MAX = 6000;

function clamp(n: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, n));
}

function computePageTargetChars(): number {
  if (typeof window === "undefined") return PAGE_TARGET_CHARS_BASE;
  const area = window.innerWidth * window.innerHeight;
  const scaled = PAGE_TARGET_CHARS_BASE * (area / PAGE_TARGET_REF_AREA);
  return Math.round(clamp(scaled, PAGE_TARGET_CHARS_MIN, PAGE_TARGET_CHARS_MAX));
}

// Window size, not the doc container's own size — the container grows/
// shrinks with however much text is currently paginated onto it, which
// would make self-measurement circular. `resize` is rAF-throttled so a
// window drag doesn't thrash re-pagination on every intermediate frame.
function usePageTargetChars(): number {
  const [target, setTarget] = useState(computePageTargetChars);

  useEffect(() => {
    if (typeof window === "undefined") return;
    let raf = 0;
    const onResize = (): void => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => setTarget(computePageTargetChars()));
    };
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      cancelAnimationFrame(raf);
    };
  }, []);

  return target;
}

// Splits an HTML string into an array of page-sized HTML chunks by walking
// the top-level block children and accumulating them until the running
// visible-text length crosses pageTargetChars. Single block elements larger
// than the target ship alone (we don't try to mid-split a paragraph).
// Sanitization runs inside so callers always get safe HTML, even on a
// single-page document.
export function paginateHtml(
  rawHtml: string,
  pageTargetChars: number = PAGE_TARGET_CHARS_BASE
): string[] {
  const trimmed = rawHtml.trim();
  if (trimmed.length === 0) return [""];

  // Server-side / non-DOM environments fall back to a single sanitized
  // page — vitest's jsdom satisfies DOMParser, but defensive anyway.
  if (typeof DOMParser === "undefined") {
    return [DOMPurify.sanitize(trimmed)];
  }

  const doc = new DOMParser().parseFromString(`<div>${trimmed}</div>`, "text/html");
  const wrapper = doc.body.firstElementChild;
  const blocks = wrapper === null ? [] : Array.from(wrapper.children);

  // If the source returned plain text (no top-level elements), treat the
  // whole thing as one page rather than producing an empty pages array.
  if (blocks.length === 0) {
    return [DOMPurify.sanitize(trimmed)];
  }

  // [draft numbering] A numbered list (a generated draft's clauses) is split
  // between its top-level items, never inside one; each piece reopens the list
  // with `start` so page 2 continues the numbering instead of restarting at 1.
  type Unit = { html: string; len: number; list: { el: Element; index: number } | null };
  const units: Unit[] = blocks.flatMap((block): Unit[] => {
    const items = /^(OL|UL)$/.test(block.tagName) ? Array.from(block.children).filter((c) => c.tagName === "LI") : [];
    if (items.length < 2 || (block.textContent?.length ?? 0) <= pageTargetChars) {
      return [{ html: block.outerHTML, len: block.textContent?.length ?? 0, list: null }];
    }
    const first = Number(block.getAttribute("start") ?? "1");
    return items.map((li, i) => ({ html: li.outerHTML, len: li.textContent?.length ?? 0, list: { el: block, index: first + i } }));
  });
  const serialize = (page: Unit[]): string => {
    let out = "";
    for (let i = 0; i < page.length; ) {
      const u = page[i]!;
      if (u.list === null) { out += u.html; i += 1; continue; }
      const el = u.list.el;
      const open = el.cloneNode(false) as Element;
      if (el.tagName === "OL" && u.list.index !== 1) open.setAttribute("start", String(u.list.index));
      let inner = "";
      while (i < page.length && page[i]!.list?.el === el) { inner += page[i]!.html; i += 1; }
      // A function, not a string: `inner` is document text, and a "$1,800,000"
      // in a replacement string reads as a back-reference ("ol,800,000").
      out += open.outerHTML.replace(/<\/(ol|ul)>$/i, (_m, tag: string) => `${inner}</${tag}>`);
    }
    return DOMPurify.sanitize(out);
  };

  const pages: string[] = [];
  let page: Unit[] = [];
  let pageLen = 0;
  for (const u of units) {
    if (page.length > 0 && pageLen + u.len > pageTargetChars) {
      pages.push(serialize(page));
      page = [];
      pageLen = 0;
    }
    page.push(u);
    pageLen += u.len;
  }
  if (page.length > 0) pages.push(serialize(page));
  return pages;
}

export interface PaginatedDocumentProps {
  /** Raw content — HTML or plain text; auto-detected and normalized. */
  content: string;
  /** Base test id; the pager derives `${testId}-prev|-next|-label` from it. */
  testId?: string;
  /** Left-aligned pager status text (e.g. "v2.1.0" or "draft · unsaved"). */
  pagerMeta?: string;
}

/** The shared read-only document renderer: sanitized, paginated HTML with a
 *  DocPager footer when there's more than one page. */
export function PaginatedDocument({
  content,
  testId = "doc",
  pagerMeta,
}: PaginatedDocumentProps): JSX.Element {
  const pageTargetChars = usePageTargetChars();
  const pages = useMemo(
    () => paginateHtml(toRenderableHtml(content), pageTargetChars),
    [content, pageTargetChars]
  );
  const [pageIdx, setPageIdx] = useState(0);

  // Reset to the first page when the document changes (route nav or
  // edit-then-save round-trip). Otherwise pageIdx could point past the
  // end of a shorter replacement doc.
  useEffect(() => {
    setPageIdx(0);
  }, [content]);

  const safeIdx = Math.min(pageIdx, pages.length - 1);
  const pageHtml = pages[safeIdx] ?? "";

  return (
    <div className="td__doc-pages" data-testid={testId}>
      <div
        className="td__doc"
        // Sanitized just above; DOMPurify strips scripts and on*= attributes.
        // Render order: sanitize → assign innerHTML.
        dangerouslySetInnerHTML={{ __html: pageHtml }}
      />
      {pages.length > 1 && (
        <DocPager
          page={safeIdx + 1}
          total={pages.length}
          onPrev={() => setPageIdx((i) => Math.max(0, i - 1))}
          onNext={() => setPageIdx((i) => Math.min(pages.length - 1, i + 1))}
          meta={pagerMeta}
          testId={testId}
        />
      )}
    </div>
  );
}
